import React, {useState, useEffect} from "react";
import {
  Box,
  Typography,
  TextField,
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select
} from "@mui/material";
import * as Yup from "yup";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { useFormik } from "formik";
import BasicModal from "../../components/BasicModal";
import { LARGE_FORM_MODAL_SX, DENSE_FORM_BODY_SX, DENSE_FIELD_PROPS } from "../../utils/formModalStyles";
import { createAdmission, getAdmissionById } from "../../services/EmergencyAdmissionService";
import { createEmergency, getByEmergencyId} from "../../services/EmergencyDetailsService";

const EmergencyAdmission = ({ open, onClose, EmergencyId, refreshList }) => {
  const [ emergencyAdmissionInfo, setEmergencyAdmissionInfo] = useState([]);
  const [ emergencyInfo, setEmergencyInfo] = useState([]);
  
  const formik = useFormik({
    initialValues: {
      patientName: "",
      patientContact: "",
      attendantName: "",
      attendantContact: "",
      patientState: "",
      status: "",
      createdAt: dayjs(),
      admissionReason: "",
      broughtBy: "",
      broughtType: "",
      firReport: "",
      firFile: null,
      ambulanceNumber: "",
      driverName: "",
    },
    onSubmit: async (values, { resetForm }) => {
      try {
        // Payload for admission
        const admissionPayload = {
          patientId: "0",
          patientName: values.patientName,
          patientContact: values.patientContact,
          attendantName: values.attendantName,
          attendantContact: values.attendantContact,
          patientState: values.patientState,
          status: '',
          createdAt: dayjs(values.createdAt).format("YYYY-MM-DDTHH:mm:ss"),
        };

        const admission = await createAdmission(admissionPayload); // returns admission.id

        // Payload for emergency details
        const emergencyDetailsPayload = {
          emergencyId: admission.id, // Use the returned id
          admissionReason: values.admissionReason,
          broughtBy: values.broughtBy,
          broughtType: values.broughtType,
          firReport: values.firReport,
          firFile: values.firFile,
          ambulanceNumber: values.ambulanceNumber,
          driverName: values.driverName,
        };

        await createEmergency(emergencyDetailsPayload); 

        resetForm();
        if (refreshList) refreshList();
        onClose();
        alert("Emergency Admission created successfully");

      } catch (error) {
        console.error(error);
        alert("Failed to submit emergency admission.");
      }
    },
    validateOnBlur: false,
    validateOnChange: false,
  });

  const fetchEmergencyAdmissionByid = async () => {
    if (!EmergencyId) {
      console.warn("EmergencyId is not provided");
      return;
    }
    try {
      // getAdmissionById uses apiClient which automatically adds organizationId as query parameter
      // This ensures data is fetched from organization-specific table (e.g., 23_emergency_admission)
      const emergencyInfo = await getAdmissionById(EmergencyId);
      setEmergencyAdmissionInfo(emergencyInfo);
      formik.setValues({
        patientName: emergencyInfo.patientName || '',
        patientContact: emergencyInfo.patientContact || '',
        attendantName: emergencyInfo.attendantName || '',
        attendantContact: emergencyInfo.attendantContact || '',
        patientState: emergencyInfo.patientState || '',
        status: '',
        createdAt: emergencyInfo.createdAt
          ? dayjs(new Date(
              emergencyInfo.createdAt[0], // year
              emergencyInfo.createdAt[1] - 1, // month (subtract 1 because backend is 1-indexed, JS Date is 0-indexed)
              emergencyInfo.createdAt[2] || 1, // day
              emergencyInfo.createdAt[3] || 0, // hour
              emergencyInfo.createdAt[4] || 0, // minute
              emergencyInfo.createdAt[5] || 0  // second
            ))
          : null,
      });
    } catch (error) {
      console.error("Failed to fetch emergency admission", error);
      setEmergencyAdmissionInfo([]);
      // Show user-friendly error message if organization mismatch
      if (error.message && error.message.includes("different organization")) {
        alert("Error: This emergency admission record belongs to a different organization and cannot be displayed.");
      }
    }
  };

  const fetchEmergencyDetailsByid = async () => {
    if (!EmergencyId) {
      console.warn("EmergencyId is not provided");
      return;
    }
    try {
      // getByEmergencyId uses apiClient which automatically adds organizationId as query parameter
      // This ensures data is fetched from organization-specific table (e.g., 23_emergency_details)
      const emergencyDetail = await getByEmergencyId(EmergencyId);
      setEmergencyInfo(emergencyDetail);
      formik.setValues({
        ...formik.values,
        broughtType: emergencyDetail.broughtType || '',
        ambulanceNumber: emergencyDetail.ambulanceNumber || '',
        driverName: emergencyDetail.driverName || '',
        firReport: emergencyDetail.firReport || '',
        admissionReason: emergencyDetail.admissionReason || '',
        broughtBy: emergencyDetail.broughtBy || '',
      });
    } catch (error) {
      console.error("Failed to fetch emergency details", error);
      setEmergencyInfo([]);
    }
  };

  useEffect(() => {
    if (open && EmergencyId) {
      fetchEmergencyAdmissionByid();
      fetchEmergencyDetailsByid();
    }
  }, [open, EmergencyId]);

  return (
    <BasicModal
      open={open}
      onClose={onClose}
      sx={LARGE_FORM_MODAL_SX}
    >
      {/* Title */}
      <Typography
        variant="h5"
        sx={{ fontSize: "1.5rem", fontWeight: "bold", textAlign: "center", mb: 2 }}
      >
        Emergency Admission Details
      </Typography>

      {/* Form body — shell (BasicModal) owns scroll when needed */}
      <Box sx={{ pr: 0.5, ...DENSE_FORM_BODY_SX }}>
        <form onSubmit={formik.handleSubmit} noValidate>
          <Grid container spacing={1}>
            {/* Patient & Attendant Info */}
            <Grid item xs={6}>
              <TextField
                label="Patient Name"
                fullWidth
                name="patientName"
                value={formik.values.patientName}
                onChange={formik.handleChange}
                error={formik.touched.patientName && Boolean(formik.errors.patientName)}
                helperText={formik.touched.patientName && formik.errors.patientName}
                InputProps={{
                  readOnly: true,
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Patient Contact"
                fullWidth
                name="patientContact"
                value={formik.values.patientContact}
                onChange={formik.handleChange}
                error={formik.touched.patientContact && Boolean(formik.errors.patientContact)}
                helperText={formik.touched.patientContact && formik.errors.patientContact}
                InputProps={{
                  readOnly: true,
                }}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                label="Attendant Name"
                fullWidth
                name="attendantName"
                value={formik.values.attendantName}
                onChange={formik.handleChange}
                error={formik.touched.attendantName && Boolean(formik.errors.attendantName)}
                helperText={formik.touched.attendantName && formik.errors.attendantName}
                InputProps={{
                  readOnly: true,
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Attendant Contact"
                fullWidth
                name="attendantContact"
                value={formik.values.attendantContact}
                onChange={formik.handleChange}
                error={formik.touched.attendantContact && Boolean(formik.errors.attendantContact)}
                helperText={formik.touched.attendantContact && formik.errors.attendantContact}
                InputProps={{
                  readOnly: true,
                }}
              />
            </Grid>

            {/* Patient State */}
            <Grid item xs={6}>
              <TextField
                select
                label="Patient State"
                fullWidth
                name="patientState"
                value={formik.values.patientState}
                onChange={formik.handleChange}
                error={formik.submitCount > 0 && Boolean(formik.errors.patientState)}
                helperText={formik.submitCount > 0 && formik.errors.patientState}
                InputProps={{
                  readOnly: true,
                }}
              >
                <MenuItem value="Critical">Critical</MenuItem>
                <MenuItem value="Serious">Serious</MenuItem>
                <MenuItem value="Stable">Stable</MenuItem>
              </TextField>
            </Grid>

            {/* Admission Date */}
            <Grid item xs={6}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Admission Date"
                  value={formik.values.createdAt}
                  onChange={(val) => formik.setFieldValue("createdAt", val)}
                  InputProps={{
                    readOnly: true,
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: Boolean(formik.errors.createdAt) && formik.submitCount > 0,
                      helperText: formik.submitCount > 0 && formik.errors.createdAt,
                    },
                  }}
                />
              </LocalizationProvider>
            </Grid>

            {/* Emergency Info */}
            <Grid item xs={6}>
              <TextField
                label="Admission Reason"
                fullWidth
                name="admissionReason"
                value={emergencyInfo.admissionReason}
                onChange={formik.handleChange}
                error={formik.touched.admissionReason && Boolean(formik.errors.admissionReason)}
                helperText={formik.touched.admissionReason && formik.errors.admissionReason}
                InputProps={{
                  readOnly: true,
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Brought By"
                fullWidth
                name="broughtBy"
                value={emergencyInfo.broughtBy}
                onChange={formik.handleChange}
                error={formik.touched.broughtBy && Boolean(formik.errors.broughtBy)}
                helperText={formik.touched.broughtBy && formik.errors.broughtBy}
                InputProps={{
                  readOnly: true,
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="How Patient Was Brought"
                fullWidth
                name="broughtType"
                value={emergencyInfo.broughtType}
                onChange={formik.handleChange}
                error={formik.touched.broughtType && Boolean(formik.errors.broughtType)}
                helperText={formik.touched.broughtType && formik.errors.broughtType}
                InputProps={{
                  readOnly: true,
                }}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                label="Police Report"
                fullWidth
                name="firReport"
                value={emergencyInfo.firReport}
                onChange={formik.handleChange}
                error={formik.touched.firReport && Boolean(formik.errors.firReport)}
                helperText={formik.touched.firReport && formik.errors.firReport}
                InputProps={{
                  readOnly: true,
                }}
              />
            </Grid>

            {formik.values.broughtType === "ambulance" && (
              <>
                <Grid item xs={6}>
                  <TextField
                    label="Ambulance Number"
                    fullWidth
                    name="ambulanceNumber"
                    value={emergencyInfo.ambulanceNumber}
                    onChange={formik.handleChange}
                    InputProps={{
                      readOnly: true,
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Driver Name"
                    fullWidth
                    name="driverName"
                    value={emergencyInfo.driverName}
                    onChange={formik.handleChange}
                    InputProps={{
                      readOnly: true,
                    }}
                  />
                </Grid>
              </>
            )}

            {formik.values.firReport === "yes" && (
              <Grid item xs={12}>
                <TextField
                  label="Upload FIR File"
                  fullWidth
                  name="firFile"
                  type="file"
                  onChange={(e) =>
                    formik.setFieldValue("firFile", e.currentTarget.files[0])
                  }
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    readOnly: true,
                  }}
                />
              </Grid>
            )}
          </Grid>
        </form>
      </Box>
    </BasicModal>
  );
};

export default EmergencyAdmission;